// Auto CYP ADHD Patient Report Helper
// ------------------------------------
// Purpose:
// Keep the CYP ADHD parent-facing Patient Report in sync with the clinician-
// approved section reports (Clinical Interview, Semi-Structured Interview,
// Diagnostic Decision, Post-Assessment Discussion). When a clinician finishes
// reviewing a section and ALL FOUR section reports are approved, and the
// previously finalised Patient Report is older than those approvals (or was
// never finalised), we re-render the Patient Report.
//
// This mirrors the Autism auto-helper (autoAutismPatientReport.js) so both
// products behave identically: no manual "Generate" button — the report just
// keeps itself current as sections are approved and re-approved.
//
// Design choices (kept in step with autoAutismPatientReport.js):
// - Read-before-write with short retry: after a section exit, Firestore server
//   timestamps can lag for a moment. We read fresh with a brief retry to avoid
//   racing that lag.
// - Strict staleness check: only regenerate if the most recent section approval
//   is newer than the Patient Report's approved-at anchor, OR an appointment
//   date was changed after that anchor.
// - Wait-then-retry for concurrency: when another render is already in-flight
//   (e.g. two sections approved in quick succession, or an MDT approving
//   together), we wait for it to finish via an onSnapshot listener, then
//   re-check staleness and trigger exactly one retry if the completed render
//   didn't capture the latest approval. Guarantees correctness without infinite
//   retry loops.
// - Fire-and-forget from UI: callers don't await this; the actual render runs
//   in the CF.
//
// Loop prevention:
// TriggerRenderCypAdhdDocTemplate (once reworked) writes
// patientReportReportApprovedAt at TRIGGER time (before the CF runs), which
// advances past every section's approval timestamp. The render CF deliberately
// never rewrites patientReportReportApprovedAt on completion, so the post-render
// recheck correctly sees "not stale" and stops.
//
// Uniform four-section gate:
// ALL four CYP ADHD sections must have their {prefix}ReportApproved flag set:
//   ciReportApproved, ssiReportApproved, ddReportApproved, padReportApproved.
// This is defined once here (via CYP_ADHD_STAGE_KEYS and STAGE_TO_REPORT_PREFIX)
// so the dashboard and auto-helper can never disagree on the gate.

import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  getPatientHeaderPathSegments,
  getCypAdhdReportContentPathSegments,
} from '../constants/cypAdhdFirestorePaths';
import {
  CYP_ADHD_STAGE_KEYS,
  STAGE_TO_REPORT_PREFIX,
} from '../constants/cypAdhdStages';
import { TriggerRenderCypAdhdDocTemplate } from './TriggerRenderCypAdhdDocTemplate';
import { waitForStageRenderComplete } from './processingStatusHelpers';

// ── Utility helpers ─────────────────────────────────────────────────────────

/**
 * Parse Firestore Timestamp | Date | ISO string into a JS Date, or null.
 * Tolerant of every shape the timestamps take across Firestore and contexts.
 */
const toDateOrNull = (value) => {
  if (!value) return null;
  try {
    if (value.toDate && typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

/**
 * Small retry helper for eventual consistency on serverTimestamp propagation.
 * Kept lightweight to avoid stressing Firestore.
 */
const retry = async (fn, attempts = 3, delaysMs = [150, 400, 800]) => {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (e) {
      lastError = e;
      if (i < attempts - 1) {
        await new Promise((res) => setTimeout(res, delaysMs[i] || 500));
      }
    }
  }
  throw lastError;
};

// ── State readers ───────────────────────────────────────────────────────────

/**
 * Read the patient header + all four CYP reportContent docs in one shot.
 * Returns { header, reportContentByStage } where reportContentByStage is
 * keyed by stage key -> doc data (or null).
 */
const readReportState = async (patientId) => {
  const headerRef = doc(db, ...getPatientHeaderPathSegments(patientId));
  const stageRefs = CYP_ADHD_STAGE_KEYS.map((stageKey) =>
    doc(db, ...getCypAdhdReportContentPathSegments(patientId, stageKey)),
  );
  const prRef = doc(db, ...getCypAdhdReportContentPathSegments(patientId, 'patientReport'));

  const [headerSnap, ...restSnaps] = await Promise.all([
    getDoc(headerRef),
    ...stageRefs.map((ref) => getDoc(ref)),
    getDoc(prRef),
  ]);

  if (!headerSnap.exists()) throw new Error('patient-header-missing');

  const reportContentByStage = {};
  CYP_ADHD_STAGE_KEYS.forEach((stageKey, i) => {
    const snap = restSnaps[i];
    reportContentByStage[stageKey] = snap.exists() ? snap.data() : null;
  });
  // The Patient Report doc is the last snap (after the 4 stage snaps).
  const prSnap = restSnaps[CYP_ADHD_STAGE_KEYS.length];
  reportContentByStage.patientReport = prSnap.exists() ? prSnap.data() : null;

  return { header: headerSnap.data(), reportContentByStage };
};

// ── Business rules ──────────────────────────────────────────────────────────

/**
 * Uniform four-section gate: every CYP ADHD stage must be approved.
 * Reads each stage's `{prefix}ReportApproved` flag.
 */
const areAllSectionsApproved = (reportContentByStage) =>
  CYP_ADHD_STAGE_KEYS.every((stageKey) => {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    return reportContentByStage?.[stageKey]?.[`${prefix}ReportApproved`] === true;
  });

/**
 * The most recent section approval time across all four stages, or null
 * if none have resolved timestamps yet.
 */
const getSectionsMaxApprovedAt = (reportContentByStage) => {
  const dates = CYP_ADHD_STAGE_KEYS.map((stageKey) => {
    const prefix = STAGE_TO_REPORT_PREFIX[stageKey];
    return toDateOrNull(reportContentByStage?.[stageKey]?.[`${prefix}ReportApprovedAt`]);
  }).filter(Boolean);
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
};

/**
 * True when the Patient Report doc's processing status is 'processing'.
 * The status field follows the CYP naming convention:
 * patientReportReportProcessingStatus (prefix 'patientReport' + 'ReportProcessingStatus').
 */
const isReportProcessing = (reportContentByStage) =>
  reportContentByStage?.patientReport?.patientReportReportProcessingStatus === 'processing';

/**
 * Appointment-date staleness: when a clinician changes a per-module
 * appointment date on the Patient Overview, the header's top-level
 * `appointmentDatesLastChangedAt` is bumped. The CYP Patient Report embeds
 * those dates, so if that clock is newer than the anchor the finalised PDF
 * is showing an out-of-date appointment date and must be re-rendered.
 *
 * This rule NEVER bootstraps a first render: it is only consulted once the
 * anchor exists.
 */
const isAppointmentDatesStale = (header, approvedAt) => {
  const changedAt = toDateOrNull(header?.appointmentDatesLastChangedAt);
  if (!changedAt || !approvedAt) return false;
  return changedAt.getTime() > approvedAt.getTime();
};

/**
 * The Patient Report is stale if it was never finalised, if any section was
 * approved more recently than the trigger-time anchor, or if an appointment
 * date was changed after that anchor.
 *
 * The anchor is `patientReportReportApprovedAt` on the Patient Report's
 * reportContent doc (written at TRIGGER time, before the CF runs).
 */
const isReportStale = (header, reportContentByStage) => {
  const approvedAt = toDateOrNull(
    reportContentByStage?.patientReport?.patientReportReportApprovedAt,
  );
  // First-time bootstrap: never finalised -> stale.
  if (!approvedAt) return true;
  const sectionsMaxApprovedAt = getSectionsMaxApprovedAt(reportContentByStage);
  // No resolved section timestamps yet -> treat as stale to allow the first render.
  if (!sectionsMaxApprovedAt) return true;
  if (approvedAt.getTime() < sectionsMaxApprovedAt.getTime()) return true;
  return isAppointmentDatesStale(header, approvedAt);
};

/**
 * Read fresh state and derive the gating booleans in one place, so the
 * wait-then-retry paths re-check against live Firestore data.
 */
const checkState = async (patientId) => {
  const { header, reportContentByStage } = await readReportState(patientId);
  return {
    header,
    reportContentByStage,
    allApproved: areAllSectionsApproved(reportContentByStage),
    stale: isReportStale(header, reportContentByStage),
    processing: isReportProcessing(reportContentByStage),
  };
};

// ── Main entry point ────────────────────────────────────────────────────────

/**
 * rerenderCypAdhdPatientReportIfStale
 * ------------------------------------
 * Safe to call (fire-and-forget) any time a clinician exits a CYP ADHD
 * section review screen. It reads the latest Firestore state and:
 *   - no-ops fast if not all four sections are approved, or the report is
 *     already up to date;
 *   - when a render is already in-flight, waits for it to finish then
 *     triggers a single retry if the report is still stale;
 *   - otherwise triggers the render, then waits + re-checks once to catch
 *     a section that was approved while the CF was running.
 *
 * @param {Object} params
 * @param {string} params.patientId
 * @param {string} params.clinicianId
 * @returns {Promise<{success: boolean, skipped?: boolean, reason?: string, error?: string}>}
 */
export const rerenderCypAdhdPatientReportIfStale = async ({ patientId, clinicianId }) => {
  if (!patientId || !clinicianId) {
    return { success: false, skipped: true, reason: 'missing-params' };
  }

  // 12-min timeout: the render CF's client timeout is 6 min, and the backend
  // reconcile sweep runs at 12 min — this must comfortably exceed the CF so
  // we don't give up before it finishes and miss the post-completion retry.
  const WAIT_TIMEOUT_MS = 12 * 60 * 1000;
  const waitForRender = () =>
    waitForStageRenderComplete(patientId, 'patientReport', WAIT_TIMEOUT_MS);

  try {
    // Read fresh (with short retry) to avoid racing the serverTimestamp
    // writes from the section approval transaction.
    const { header, reportContentByStage } = await retry(
      () => readReportState(patientId), 3,
    );

    // Gate: every section report must be approved.
    if (!areAllSectionsApproved(reportContentByStage)) {
      return { success: true, skipped: true, reason: 'not-all-approved' };
    }
    // Gate: skip if the Patient Report is already up to date.
    if (!isReportStale(header, reportContentByStage)) {
      return { success: true, skipped: true, reason: 'not-stale' };
    }

    // If another caller is already rendering, wait for it, then re-check
    // and trigger one retry if the completed render didn't capture our
    // latest approval.
    if (isReportProcessing(reportContentByStage)) {
      console.log('CYP patient report already processing — waiting before re-checking staleness');
      await waitForRender();

      const recheck = await checkState(patientId);
      if (!recheck.stale || recheck.processing || !recheck.allApproved) {
        return { success: true, skipped: true, reason: 'resolved-after-wait' };
      }

      console.log('CYP patient report still stale after in-flight render — triggering retry');
      return await TriggerRenderCypAdhdDocTemplate({
        patientId,
        stageKey: 'patientReport',
        clinicianId,
      });
    }

    // Happy path: not currently processing and report is stale — trigger the render.
    const result = await TriggerRenderCypAdhdDocTemplate({
      patientId,
      stageKey: 'patientReport',
      clinicianId,
    });

    // After the render completes, re-check once: another section may have
    // been approved while the CF was running. Trigger exactly one retry if
    // still stale.
    try {
      await waitForRender();
      const recheck = await checkState(patientId);
      if (recheck.stale && !recheck.processing && recheck.allApproved) {
        console.log('CYP patient report stale after render — triggering one retry');
        await TriggerRenderCypAdhdDocTemplate({
          patientId,
          stageKey: 'patientReport',
          clinicianId,
        });
      }
    } catch (retryErr) {
      console.error('Post-render staleness recheck failed:', retryErr);
    }

    return result;
  } catch (error) {
    console.error('rerenderCypAdhdPatientReportIfStale error:', error);
    return { success: false, error: error.message || 'unknown' };
  }
};

// Exported for unit tests only.
export const __testOnly = {
  toDateOrNull,
  readReportState,
  areAllSectionsApproved,
  getSectionsMaxApprovedAt,
  isReportProcessing,
  isAppointmentDatesStale,
  isReportStale,
  checkState,
};
